import * as React from 'react';
import type { ReactNode } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';
import { useReducedMotion } from '@mds/shared/hooks/use-reduced-motion';

/**
 * How much of the rail one item takes at each width: one on a phone, two from
 * `sm`, three from `lg`.
 *
 * From `sm` up it is `(100% - the gaps between them) / n` — the widest an item
 * can be with n of them whole, and nothing of the next one showing. A plain
 * share of the rail cannot land on that: three at 32% comes to 96% plus two
 * 2rem gaps, which is over the edge, so the third item is the one cut off.
 *
 * The phone width keeps a sliver of the next item deliberately, because that is
 * the only affordance it has — the arrows need margin to stand in and there is
 * none below `md`, so a rail that ends flush with the frame looks like the
 * whole row and nobody swipes it.
 *
 * The literal `rem`s are `gap-x-8` counted out. Change the gap through
 * `trackClassName` and these want changing with it.
 */
const COLUMNS = [
  'auto-cols-[calc(100%-3.5rem)]',
  'sm:auto-cols-[calc((100%-2rem)/2)]',
  'lg:auto-cols-[calc((100%-4rem)/3)]',
].join(' ');

/**
 * Slack, in pixels, when deciding whether an end has been reached.
 *
 * `scrollLeft` is fractional on a zoomed or fractionally-scaled display, so the
 * last scroll of a rail routinely stops at `scrollWidth - clientWidth - 0.4`.
 * Compared exactly, the next arrow would stay enabled on a rail that has
 * visibly run out.
 */
const EDGE_SLACK = 1;

type Rail = {
  /** Whether there is anything to scroll to at all. No overflow, no arrows. */
  overflows: boolean;
  atStart: boolean;
  atEnd: boolean;
  /**
   * Where the arrows sit, in pixels from the top of the rail — see `align`.
   * `null` means "no measurement", and the arrows fall back to the middle.
   *
   * Measured as the gap between two rectangles rather than computed from the
   * item's own height, because everything between the rail's top edge and the
   * thing being aligned to is a moving part: the track's padding, its negative
   * margin, and whether that margin collapses with whatever the caller passed
   * in `className`. Two `getBoundingClientRect` calls answer all of it at once
   * and cannot drift out of step with the classes below — which a figure
   * derived from `h-52.5` and `pt-3` silently would, the first time one of
   * those changed.
   */
  armY: number | null;
};

const AT_REST: Rail = { overflows: false, atStart: true, atEnd: true, armY: null };

export type ScrollRailProps = {
  /** The items. Each direct child becomes one column of the rail. */
  children: ReactNode;
  /**
   * Names the rail for anyone who cannot see it — "Our spaces", not "carousel".
   * Required: a scrollable group with no name is one a screen reader announces
   * as "group" and you have to enter to identify.
   */
  label: string;
  /**
   * What the arrows line up with vertically. Both answers are measured off the
   * first item; neither is "the middle of the rail", which is not the middle of
   * anything a visitor can see — the rail is as tall as the items *plus* any
   * margin they carry, and `MediaCard`'s is 3rem, which drops the arrows a good
   * inch below the row they belong to.
   *
   * `item` — the middle of an item. The default, and right for anything of one
   * piece: a logo strip, a row of quotes, a set of plain tiles.
   *
   * `media` — the middle of an item's *first* child, which on a card is its
   * picture. Right for anything that is a fixed-height image with a variable
   * number of lines underneath it, where the middle of the item lands somewhere
   * in the third line of a description and moves as soon as a title wraps.
   */
  align?: 'item' | 'media';
  /**
   * Number of grid rows the rail lays out, for items that align with each other
   * across the row via `grid-rows-subgrid`. Leave it unset for ordinary items,
   * which take one row each.
   */
  rows?: number;
  /**
   * Classes for the scrolling track, which is where the column widths and the
   * gap live — pass `auto-cols-*` to change how many items are on screen, or
   * `gap-x-*` to change the spacing between them.
   *
   * Per breakpoint: the defaults set a width at base, `sm` and `lg`, and
   * overriding one variant leaves the other two in place.
   */
  trackClassName?: string;
  className?: string;
};

/**
 * A row of anything that scrolls sideways, with prev/next arrows that move it
 * by one item.
 *
 * Content-agnostic on purpose — cards, pictures, quotes, logos. It owns the
 * scrolling, the arrows and their states; what an item looks like is the
 * caller's business, and how wide one is comes from `trackClassName`.
 *
 * The scrolling is the browser's own: an overflow container with scroll snap,
 * not a transform stack. That is what lets it stay out of the items' way (a
 * carousel library wraps every child in containers of its own, which breaks
 * any child that has to line up with its neighbours — see `rows`), and it
 * means the trackpad, the touch swipe, the scrollbar and the arrow keys all
 * work without this file knowing about any of them. The arrows are the
 * mouse-only path onto the same mechanism: they call `scrollBy`, and the snap
 * points do the landing.
 */
export default function ScrollRail({
  children,
  label,
  align = 'item',
  rows,
  trackClassName,
  className,
}: ScrollRailProps) {
  const railRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const [rail, setRail] = React.useState<Rail>(AT_REST);

  const measure = React.useCallback(() => {
    const track = trackRef.current;
    const railEl = railRef.current;
    if (!track || !railEl) return;

    const furthest = track.scrollWidth - track.clientWidth;

    // The first item, or — aligning to media — the first thing inside it.
    //
    // An item's rect and not the rail's, because a rect excludes the element's
    // own margins: this is the middle of the thing on screen, not the middle of
    // the space it was laid out in. That difference is the whole reason the
    // arrows used to sit low.
    const item = track.firstElementChild;
    const anchor = align === 'media' ? (item?.firstElementChild ?? item) : item;

    let armY: number | null = null;
    if (anchor) {
      const box = anchor.getBoundingClientRect();
      armY = box.top + box.height / 2 - railEl.getBoundingClientRect().top;
    }

    const next: Rail = {
      overflows: furthest > EDGE_SLACK,
      atStart: track.scrollLeft <= EDGE_SLACK,
      atEnd: track.scrollLeft >= furthest - EDGE_SLACK,
      armY,
    };

    // This runs on every scroll event, and all four answers are the same for
    // most of a swipe. Handing React a fresh object each time would re-render
    // the whole rail — every item in it — at frame rate for nothing.
    setRail((current) =>
      current.overflows === next.overflows &&
      current.atStart === next.atStart &&
      current.atEnd === next.atEnd &&
      current.armY === next.armY
        ? current
        : next,
    );
  }, [align]);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    measure();
    track.addEventListener('scroll', measure, { passive: true });

    // A resize observer rather than a window listener, because most of what
    // changes the answer here is not the window: a font loading, a title
    // wrapping onto a second line, the list itself growing. Watching the track
    // catches all of those and the window resize as well.
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    // The first item too, and the thing inside it the arrows line up with: one
    // gives the step they move by, the other gives the height they sit at, and
    // neither is something the track's own size can be relied on to predict.
    const item = track.firstElementChild;
    if (item) observer.observe(item);
    if (item?.firstElementChild) observer.observe(item.firstElementChild);

    return () => {
      track.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure]);

  function step(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;

    // Measured rather than configured. The item width comes from a breakpoint
    // and the gap from a utility class, so the only place both are known for
    // certain is the DOM — and reading them means the arrows keep moving by
    // exactly one item when either changes.
    const item = track.firstElementChild;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = item ? item.getBoundingClientRect().width + gap : track.clientWidth;

    track.scrollBy({
      left: direction * distance,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  return (
    // The arrows stand in reserved margin rather than on top of the items: a
    // control overlapping the picture it scrolls is one you cannot tell from
    // part of that picture, and it covers the corner of an item that is often
    // itself a link.
    //
    // `px-18` is the arrow (`size-10`, 2.5rem) plus 2rem of air, which is the
    // same gap the items keep from each other — so an arrow reads as another
    // column of the row rather than as something parked beside it. From `md`
    // up only: a phone has no width to give away, and there the swipe is the
    // gesture anyway, so the rail runs the full width of its container and the
    // arrows stay out of it.
    <div ref={railRef} className={cn('relative md:px-18', className)}>
      <div
        ref={trackRef}
        // `group` rather than `region`: this is a set of items, and a landmark
        // per rail would clutter the page's landmark list for no gain.
        //
        // No `tabIndex` on the scroller, deliberately. A scroll container needs
        // a keyboard route to everything in it, and the items here are links:
        // tabbing through them scrolls the rail along as focus moves into each
        // one. Making the container focusable as well would add a tab stop in
        // front of every rail that lands on nothing.
        role="group"
        aria-label={label}
        className={cn(
          'grid grid-flow-col gap-x-8 gap-y-0 overflow-x-auto overscroll-x-contain',
          COLUMNS,
          'snap-x snap-mandatory *:snap-start',
          // `overflow-x` makes the box clip vertically too, whatever the Y
          // value says — so an item that lifts on hover, or carries a shadow,
          // would be cut off along the top edge. The padding gives it room and
          // the negative margin takes that back off the layout, leaving the
          // rail sitting exactly where it would have without either.
          'pt-3 -mt-3',
          // The arrows are the affordance on a mouse and the swipe is the
          // affordance on a touch screen; a scrollbar across the bottom of a
          // photograph is neither, and the design does not have one.
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          trackClassName,
        )}
        // `auto` tracks, so a row of items can share them through
        // `grid-rows-subgrid` and line their insides up with each other.
        style={rows ? { gridTemplateRows: `repeat(${rows}, auto)` } : undefined}
      >
        {children}
      </div>

      {rail.overflows && (
        <>
          <RailControl
            label={`Scroll ${label} left`}
            onClick={() => step(-1)}
            disabled={rail.atStart}
            armY={rail.armY}
            className="left-0"
          >
            <LuChevronLeft aria-hidden="true" className="size-5" />
          </RailControl>

          <RailControl
            label={`Scroll ${label} right`}
            onClick={() => step(1)}
            disabled={rail.atEnd}
            armY={rail.armY}
            className="right-0"
          >
            <LuChevronRight aria-hidden="true" className="size-5" />
          </RailControl>
        </>
      )}
    </div>
  );
}

/**
 * One arrow.
 *
 * `armY` is how far down the rail it sits, measured off the item it lines up
 * with. Without one — the first render, before anything has been measured — it
 * falls back to halfway down the rail, which is near enough that nothing jumps
 * when the real figure lands a frame later.
 */
function RailControl({
  label,
  onClick,
  disabled,
  armY,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  armY: number | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      style={{ top: armY ?? '50%' }}
      className={cn(
        // Hidden where there is no margin to stand in — see the reserved
        // padding on the rail.
        'absolute z-10 hidden size-10 -translate-y-1/2 place-items-center md:grid',
        'rounded-button border border-hairline bg-surface text-ink shadow-card',
        'transition-[color,background-color,border-color,opacity]',
        'duration-(--duration-fast) ease-standard motion-reduce:transition-none',
        'hover:border-brand-edge hover:text-brand-text',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        // Dimmed rather than removed at the ends: an arrow that disappears when
        // you reach the last item takes the other one with it, because the row
        // reflows under the pointer you were about to click with.
        'disabled:border-hairline disabled:text-ink-disabled disabled:opacity-60',
        className,
      )}
    >
      {children}
    </button>
  );
}
